import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiExternalLink,
  FiMail,
  FiMapPin,
  FiUser,
  FiPackage,
  FiX,
} from 'react-icons/fi';
import request from '../utils/request';
import { formatDate } from '../utils/formatDate';

// Post-approval refund operations queue.
//
// This queue is deliberately separate from `RefundApprovalQueue`:
//   - RefundApprovalQueue handles the *decision* (approve / reject a refund).
//   - RefundOpsQueue handles the *payout* — staff manually issue a refund in
//     Stripe, then click "Mark refunded" so the queue reflects reality.
//
// An order appears here if it has at least one product with
// `refunded === true && refundApproved === true`. It is "Pending" until
// `order.guestRefund.issued === true` is set by the backend, at which point
// it moves to "Refunded".

function formatCurrency(n) {
  const value = Number(n) || 0;
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function productLineTotal(p) {
  const price = Number(p?.price) || 0;
  const qty = Number(p?.quantity) || 1;
  return price * qty;
}

function getRefundedApprovedProducts(order) {
  const out = [];
  (order?.products || []).forEach((p, idx) => {
    if (p?.refunded === true && p?.refundApproved === true) {
      out.push({ product: p, productIndex: idx });
    }
  });
  return out;
}

function latestApprovedAt(products) {
  let latest = null;
  products.forEach(({ product }) => {
    const at = product?.refundApprovedAt || product?.refundedAt;
    if (!at) return;
    const ts = new Date(at).getTime();
    if (!latest || ts > latest) latest = ts;
  });
  return latest ? new Date(latest) : null;
}

export default function RefundOpsQueue({
  orders = [],
  completedOrders = [],
  hosts = [],
  onOpenOrder,
  onOrderUpdated,
}) {
  const { adminInfo } = useSelector((s) => s.login);
  const [tab, setTab] = useState('pending');
  const [processingId, setProcessingId] = useState(null);
  const [error, setError] = useState('');
  const [amountOverrides, setAmountOverrides] = useState({});

  const rows = useMemo(() => {
    const merged = [...orders, ...completedOrders];
    const entries = [];
    merged.forEach((order) => {
      const refunded = getRefundedApprovedProducts(order);
      if (refunded.length === 0) return;
      const total = refunded.reduce((sum, r) => sum + productLineTotal(r.product), 0);
      const issued = order?.guestRefund?.issued === true;
      entries.push({
        order,
        refunded,
        total,
        issued,
        approvedAt: latestApprovedAt(refunded),
      });
    });
    entries.sort((a, b) => {
      const aAt = a.issued
        ? new Date(a.order?.guestRefund?.issuedAt || 0).getTime()
        : (a.approvedAt ? a.approvedAt.getTime() : 0);
      const bAt = b.issued
        ? new Date(b.order?.guestRefund?.issuedAt || 0).getTime()
        : (b.approvedAt ? b.approvedAt.getTime() : 0);
      return bAt - aAt;
    });
    return entries;
  }, [orders, completedOrders]);

  const pendingRows = useMemo(() => rows.filter((r) => !r.issued), [rows]);
  const refundedRows = useMemo(() => rows.filter((r) => r.issued), [rows]);
  const visibleRows = tab === 'pending' ? pendingRows : refundedRows;

  const handleMark = async (entry, shouldIssue) => {
    const key = entry.order._id;
    setProcessingId(key);
    setError('');
    try {
      const override = amountOverrides[key];
      const amount = typeof override === 'number' ? override : entry.total;
      const payload = {
        issued: shouldIssue,
        amount: shouldIssue ? amount : undefined,
      };
      const res = await request.patch(`/orders/${key}/guest-refund`, payload);
      if (onOrderUpdated && res?.data) {
        onOrderUpdated(res.data);
      } else if (onOrderUpdated) {
        // Optimistic fallback if backend didn't return populated doc.
        onOrderUpdated({
          ...entry.order,
          guestRefund: {
            ...(entry.order.guestRefund || {}),
            issued: shouldIssue,
            issuedAt: shouldIssue ? new Date().toISOString() : null,
            issuedByName: shouldIssue
              ? `${adminInfo?.firstName || ''} ${adminInfo?.lastName || ''}`.trim()
              : '',
            amount: shouldIssue ? amount : undefined,
          },
        });
      }
    } catch (err) {
      console.error('Failed to update guest refund status', err);
      setError(err?.response?.data?.message || 'Failed to update refund status');
    } finally {
      setProcessingId(null);
    }
  };

  const totalPending = useMemo(
    () => pendingRows.reduce((sum, r) => sum + (r.total || 0), 0),
    [pendingRows]
  );

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-300">
            <FiDollarSign className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Refund Operations</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Approved refunds waiting to be issued manually in Stripe
            </p>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-4 text-xs">
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
            <span className="h-2 w-2 rounded-full bg-amber-500" />
            <span className="font-semibold">{pendingRows.length}</span>
            <span className="text-gray-500 dark:text-gray-400">pending</span>
            {pendingRows.length > 0 && (
              <span className="text-gray-400 dark:text-gray-500">·</span>
            )}
            {pendingRows.length > 0 && (
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {formatCurrency(totalPending)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            <span className="font-semibold">{refundedRows.length}</span>
            <span className="text-gray-500 dark:text-gray-400">refunded</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40">
        {[
          { id: 'pending', label: 'Pending', count: pendingRows.length },
          { id: 'refunded', label: 'Refunded', count: refundedRows.length },
        ].map((t) => {
          const isActive = tab === t.id;
          const toneActive =
            t.id === 'pending'
              ? 'bg-amber-500 text-white'
              : 'bg-emerald-500 text-white';
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                isActive
                  ? toneActive
                  : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              <span>{t.label}</span>
              <span
                className={`rounded-full px-1.5 text-[10px] font-bold ${
                  isActive
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                }`}
              >
                {t.count}
              </span>
            </button>
          );
        })}
      </div>

      {error ? (
        <div className="flex items-center justify-between px-5 py-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-xs">
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-3">
            <FiX className="h-4 w-4" />
          </button>
        </div>
      ) : null}

      {visibleRows.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <FiPackage className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {tab === 'pending' ? 'No refunds waiting to be issued.' : 'No refunds have been issued yet.'}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {visibleRows.map((entry) => {
            const order = entry.order;
            const host = hosts.find((h) => h._id === order.property?.hostID) || {};
            const hostName = host.firstName
              ? `${host.firstName} ${host.lastName}`.trim()
              : order?.property?.hostName || '—';
            const addr = order?.property?.address;
            const addressLine = addr
              ? [addr.line1, addr.city, addr.state].filter(Boolean).join(', ')
              : order?.userInputAddress || '—';
            const isProcessing = processingId === order._id;
            const overrideAmount = amountOverrides[order._id];
            const issuedInfo = order?.guestRefund;

            return (
              <div
                key={order._id}
                className="px-5 py-4 hover:bg-gray-50/50 dark:hover:bg-gray-800/40 transition-colors"
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-[11px] font-semibold text-gray-700 dark:text-gray-300">
                        #{order.uniqueId}
                      </span>
                      {entry.issued ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
                          <FiCheckCircle className="h-3 w-3" /> Refunded
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-300">
                          <FiClock className="h-3 w-3" /> Pending manual refund
                        </span>
                      )}
                      {order.inStockDate ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                          In-stock {formatDate(order.inStockDate)}
                        </span>
                      ) : null}
                    </div>

                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <FiMapPin className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                        <span className="truncate">{addressLine}</span>
                      </div>
                      <div className="flex items-center gap-1.5 min-w-0">
                        <FiUser className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                        <span className="truncate">{hostName}</span>
                      </div>
                      <div className="flex items-center gap-1.5 min-w-0">
                        <FiMail className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                        <span className="truncate">{order.guestEmail || '—'}</span>
                      </div>
                      {entry.issued && issuedInfo?.issuedAt ? (
                        <div className="flex items-center gap-1.5 min-w-0 text-emerald-600 dark:text-emerald-400">
                          <FiCheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate">
                            Issued {formatDate(issuedInfo.issuedAt)}
                            {issuedInfo.issuedByName ? ` · ${issuedInfo.issuedByName}` : ''}
                          </span>
                        </div>
                      ) : null}
                    </div>

                    <div className="mt-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-800/40 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          {entry.refunded.length} item{entry.refunded.length > 1 ? 's' : ''} to refund
                        </span>
                        <span className="text-sm font-bold text-gray-900 dark:text-white">
                          {formatCurrency(entry.total)}
                        </span>
                      </div>
                      <ul className="space-y-1">
                        {entry.refunded.map(({ product, productIndex }) => (
                          <li
                            key={`${order._id}-${productIndex}`}
                            className="flex items-start justify-between gap-3 text-xs"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-gray-800 dark:text-gray-200 font-medium">
                                {product?.name || product?.title || 'Item'}
                              </div>
                              <div className="text-[11px] text-gray-500 dark:text-gray-400">
                                Qty {Number(product?.quantity) || 1}
                                {product?.refundReason ? ` · ${product.refundReason}` : ''}
                                {product?.refundApprovedByName
                                  ? ` · approved by ${product.refundApprovedByName}`
                                  : ''}
                              </div>
                            </div>
                            <span className="text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">
                              {formatCurrency(productLineTotal(product))}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="flex flex-col items-stretch gap-2 w-full lg:w-60 flex-shrink-0">
                    {!entry.issued ? (
                      <>
                        <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          Refund amount
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={
                              typeof overrideAmount === 'number'
                                ? overrideAmount
                                : entry.total.toFixed(2)
                            }
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              setAmountOverrides((prev) => ({
                                ...prev,
                                [order._id]: Number.isFinite(v) ? v : 0,
                              }));
                            }}
                            className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 pl-7 pr-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                        <button
                          onClick={() => handleMark(entry, true)}
                          disabled={isProcessing}
                          className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold px-3 py-2 transition-colors"
                        >
                          <FiCheckCircle className="h-4 w-4" />
                          {isProcessing ? 'Saving...' : 'Mark refunded'}
                        </button>
                        {onOpenOrder ? (
                          <button
                            onClick={() => onOpenOrder(order._id)}
                            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 text-xs font-medium px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            <FiExternalLink className="h-3.5 w-3.5" />
                            Open order
                          </button>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-3 text-xs">
                          <div className="font-semibold text-emerald-700 dark:text-emerald-300">
                            {formatCurrency(issuedInfo?.amount ?? entry.total)} refunded
                          </div>
                          {issuedInfo?.issuedByName ? (
                            <div className="text-emerald-600/80 dark:text-emerald-400/80">
                              by {issuedInfo.issuedByName}
                            </div>
                          ) : null}
                        </div>
                        <button
                          onClick={() => handleMark(entry, false)}
                          disabled={isProcessing}
                          className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 text-xs font-medium px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        >
                          {isProcessing ? 'Saving...' : 'Undo'}
                        </button>
                        {onOpenOrder ? (
                          <button
                            onClick={() => onOpenOrder(order._id)}
                            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 text-xs font-medium px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            <FiExternalLink className="h-3.5 w-3.5" />
                            Open order
                          </button>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
